// Read the (untrusted) payload of a media token to recover the EXACT callId/peerId strings
// the media-service will verify against (media-service auth/mediaToken.ts).
//
// Why this exists: app-service mints the media token with peerId = userId.ToString() at full
// 64-bit precision, but the same userId reaches the browser as a JSON *number* (SignalR
// RegisterResult.UserId is a long). A userId above 2^53 (e.g. the Identity module's 63-bit
// StableId, ~5.05e18) is rounded when parsed, so String(me.userId) no longer equals the
// token's peerId. The media-service then rejects the websocket ("invalid media token") and
// that peer never joins the room. Deriving the ids from the token itself sidesteps the
// rounding entirely: the value used in the handshake is byte-for-byte the value the server
// signed. No signature check here - the media-service still verifies the token.
//
// Token format: base64url(payloadJson) + "." + base64url(HMAC-SHA256(payloadB64, secret)).

export interface MediaTokenIds {
  callId?: string;
  peerId?: string;
}

export function decodeMediaTokenIds(token: string): MediaTokenIds {
  const dot = token.indexOf(".");
  const payloadB64 = dot > 0 ? token.slice(0, dot) : "";
  if (!payloadB64) return {};
  try {
    const obj = JSON.parse(base64UrlDecode(payloadB64)) as { callId?: unknown; peerId?: unknown };
    const ids: MediaTokenIds = {};
    // Only accept non-empty strings; the token carries ids as strings precisely to preserve
    // precision, so a numeric/other type means a malformed token we should not trust.
    if (typeof obj.callId === "string" && obj.callId) ids.callId = obj.callId;
    if (typeof obj.peerId === "string" && obj.peerId) ids.peerId = obj.peerId;
    return ids;
  } catch {
    return {};
  }
}

function base64UrlDecode(b64url: string): string {
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(b64url.length / 4) * 4, "=");
  const binary = atob(b64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
